import {
  ArrowUpRight,
  BookOpen,
  Check,
  ClipboardCheck,
  FileSignature,
  Shield,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { LoyaltyWelcomeModal } from '@/components';
import { PortalOutletContext } from '@/components/portal/PortalShellContainer';
import { useAuth, useReservation } from '@/contexts';
import { PortalItem, PortalItemKey } from '@/lib/api';
import { LoyaltyProgram } from '@/types';

// Translation-aware META getter. Keep strings as literals inside t() so the
// i18next-parser can extract them at build time.
function getMeta(
  key: PortalItemKey,
  t: (s: string) => string
): { icon: typeof BookOpen; blurb: string; cta: string; group: 'prep' | 'stay' } {
  switch (key) {
    case 'rental_agreement':
      return {
        icon: FileSignature,
        blurb: t('Sign off on the house rules so your host can share your check-in details'),
        cta: t('Sign agreement'),
        group: 'prep',
      };
    case 'check_in_form':
      return {
        icon: ClipboardCheck,
        blurb: t('Share a few details about your arrival: who, when, and anything special'),
        cta: t('Fill in details'),
        group: 'prep',
      };
    case 'damage_waiver':
      return {
        icon: Shield,
        blurb: t('Pick a coverage option to protect against accidental damage'),
        cta: t('Choose coverage'),
        group: 'prep',
      };
    case 'guidebook':
      return {
        icon: BookOpen,
        blurb: t('House manual and neighborhood favorites, curated by your host'),
        cta: t('Open guidebook'),
        group: 'stay',
      };
    case 'upsells':
      return {
        icon: Sparkles,
        blurb: t('Upgrade your stay'),
        cta: t('Browse add-ons'),
        group: 'stay',
      };
  }
}

const GROUP_FOR_KEY: Record<PortalItemKey, 'prep' | 'stay'> = {
  rental_agreement: 'prep',
  check_in_form: 'prep',
  damage_waiver: 'prep',
  guidebook: 'stay',
  upsells: 'stay',
};

const SLUG_FOR_KEY: Record<PortalItemKey, string> = {
  rental_agreement: 'rental-agreement',
  damage_waiver: 'damage-waiver',
  check_in_form: 'check-in',
  guidebook: 'guidebook',
  upsells: 'add-ons',
};

function greetingForHour(h: number, t: (s: string) => string): string {
  if (h < 5) return t('Good evening');
  if (h < 12) return t('Good morning');
  if (h < 18) return t('Good afternoon');
  return t('Good evening');
}

function firstName(fullName: string | undefined): string {
  if (!fullName) return '';
  return fullName.trim().split(/\s+/)[0] || '';
}

function parseLocalDate(input: string | undefined | null): Date | null {
  if (!input) return null;
  const m = String(input).match(/^(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  const [y, mo, d] = m[1].split('-').map(Number);
  return new Date(y, mo - 1, d);
}

function daysUntil(target: Date): number {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// Mirrors Layout.tsx's formatDate/formatTime so the overview dates match the
// section pages (e.g. "Fri, Apr 24, 2026" + "at 4:00 PM").
function formatLongDate(d: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

function formatTimeOfDay(timestamp: string | null | undefined, timezone?: string | null): string {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (timezone) options.timeZone = timezone;
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch {
    return '';
  }
}

function countdownLabel(days: number, t: (s: string, vars?: Record<string, unknown>) => string): string {
  if (days > 1) return t('in {{count}} days', { count: days });
  if (days === 1) return t('tomorrow');
  if (days === 0) return t('today');
  return t('under way');
}

function loyaltyDismissedKey(reservationId: string | undefined): string | null {
  if (!reservationId) return null;
  return `loyalty_modal_dismissed_${reservationId}`;
}

// Best booking-total percent discount across all tiers (generic over the
// tiered taxonomy: a perk is a booking discount when its benefitKey maps to a
// booking_total target in the program's catalog).
function getDiscountPct(program: LoyaltyProgram): number | null {
  const catalog = program.config.targetCatalog ?? {};
  let max: number | null = null;
  for (const tier of program.config.tiers) {
    for (const perk of tier.perks) {
      if (perk.type !== 'discount' || perk.value.mode !== 'percent') continue;
      const targets = catalog[perk.benefitKey] ?? [];
      if (!targets.some((target) => target.kind === 'booking_total')) continue;
      const pct = perk.value.amount ?? 0;
      if (pct > 0 && (max === null || pct > max)) max = pct;
    }
  }
  return max;
}

export default function Portal() {
  const { t } = useTranslation('portal');
  const { payload } = useAuth();
  const navigate = useNavigate();
  const { summary } = useReservation();
  const { items, loading, error, loyalty } = useOutletContext<PortalOutletContext>();

  const loyaltyProgram = loyalty.program;
  const loyaltyAlreadyEnrolled = loyalty.enrolledMembership !== null;
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  // Guards the auto-open effect from re-firing when PortalShellContainer
  // refetches loyalty on tab focus and produces a new program object reference.
  // Without this, a plain-X dismiss (which intentionally doesn't persist) would
  // reopen the modal within the same session.
  const loyaltyModalAutoOpenedRef = useRef(false);

  useEffect(() => {
    document.title = t('Guest portal');
  }, [t]);

  // First-time modal: per-reservation localStorage gate. Plain close does NOT
  // persist (returns next visit); "Don't show again" persists. Suppressed
  // outright once the guest is enrolled.
  useEffect(() => {
    if (loyaltyModalAutoOpenedRef.current) return;
    if (!loyaltyProgram || loyaltyAlreadyEnrolled) return;
    const key = loyaltyDismissedKey(payload?.reservationId);
    if (!key) return;
    if (localStorage.getItem(key) === '1') return;
    loyaltyModalAutoOpenedRef.current = true;
    setShowLoyaltyModal(true);
  }, [loyaltyProgram, loyaltyAlreadyEnrolled, payload?.reservationId]);

  const loyaltyDiscount = loyaltyProgram ? getDiscountPct(loyaltyProgram) : null;

  const arrival = parseLocalDate(summary?.arrivalDate);
  const departure = parseLocalDate(summary?.departureDate);
  const daysOut = arrival ? daysUntil(arrival) : null;

  const prepItems = useMemo(() => items.filter((i) => GROUP_FOR_KEY[i.key] === 'prep'), [items]);
  const stayItems = useMemo(() => items.filter((i) => GROUP_FOR_KEY[i.key] === 'stay'), [items]);

  const progress = useMemo(() => {
    const completed = prepItems.filter((i) => i.status === 'completed').length;
    return { completed, total: prepItems.length };
  }, [prepItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-7 w-7 border-2 border-zinc-200 border-t-[#1E3A5F]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 lg:px-10 py-16 max-w-xl">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  const greeting = greetingForHour(new Date().getHours(), t);
  const name = firstName(summary?.guestName);
  const arrivalDateStr = arrival ? formatLongDate(arrival) : null;
  const departureDateStr = departure ? formatLongDate(departure) : null;
  const arrivalTimeStr = formatTimeOfDay(summary?.arrivalTime, summary?.timezone);
  const departureTimeStr = formatTimeOfDay(summary?.departureTime, summary?.timezone);

  return (
    <>
      {/* ─── Hero ──────────────────────────────────────────── */}
      <section className="relative">
        <div className="relative aspect-[16/9] md:aspect-[21/9] lg:aspect-[24/9] w-full overflow-hidden bg-zinc-900">
          {summary?.listingAvatarUrl ? (
            <img
              src={summary.listingAvatarUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#2a4a75] to-[#1E3A5F]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />

          <div className="absolute inset-x-0 bottom-0 px-6 lg:px-10 pb-8 lg:pb-10">
            <div className="max-w-3xl">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
                {daysOut !== null && daysOut >= 0
                  ? t('Check-in {{when}}', { when: countdownLabel(daysOut, t) })
                  : t('Your stay')}
              </p>
              <h1 className="mt-3 text-white text-[34px] sm:text-[40px] lg:text-[52px] font-semibold leading-[1.05] tracking-tight">
                {greeting}
                {name ? `, ${name}` : ''}.
              </h1>
              <p className="mt-2 text-white/80 text-base sm:text-lg">
                {summary?.listingName || t('Welcome to your stay')}
              </p>
            </div>
          </div>
        </div>

        {/* Dates strip + progress */}
        <div className="px-4 sm:px-6 lg:px-10 -mt-6 lg:-mt-8 relative z-10">
          <div className="max-w-3xl bg-white rounded-2xl border border-zinc-200/70 shadow-sm p-5 lg:p-6 flex items-center gap-6 lg:gap-8">
            {arrivalDateStr && (
              <DateBlock label={t('Check-in')} dateStr={arrivalDateStr} timeStr={arrivalTimeStr} />
            )}
            <div className="h-12 w-px bg-zinc-200 self-stretch" />
            {departureDateStr && (
              <DateBlock label={t('Check-out')} dateStr={departureDateStr} timeStr={departureTimeStr} />
            )}

            {progress.total > 0 && (
              <>
                <div className="hidden sm:block h-12 w-px bg-zinc-200" />
                <div className="hidden sm:flex flex-col flex-1 min-w-0">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-500">
                      {progress.completed === progress.total ? t('All set') : t('To do')}
                    </span>
                    <span className="text-[11px] text-zinc-500 tabular-nums">
                      {progress.completed}/{progress.total}
                    </span>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-zinc-100 overflow-hidden">
                    <div
                      className="h-full bg-[#1E3A5F] transition-all duration-700"
                      style={{
                        width: `${Math.round((progress.completed / Math.max(progress.total, 1)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ─── Prep tasks ────────────────────────────────────── */}
      {prepItems.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-10 pt-12 lg:pt-16">
          <SectionHeader
            eyebrow={t('Before you arrive')}
            title={progress.completed === progress.total ? t('You’re all set') : t('A few things to finish')}
            progress={progress}
          />
          <div className="mt-6 bg-white rounded-2xl border border-zinc-200/70 overflow-hidden">
            {prepItems.map((item, idx) => (
              <TaskRow
                key={item.key}
                item={item}
                first={idx === 0}
                last={idx === prepItems.length - 1}
                onClick={() => navigate(`/portal/home/${SLUG_FOR_KEY[item.key]}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* ─── Stay items — same row style as prep for visual consistency ─── */}
      {(stayItems.length > 0 || loyaltyProgram) && (
        <section className="px-4 sm:px-6 lg:px-10 pt-12 lg:pt-16">
          <SectionHeader eyebrow={t('While you’re here')} title={t('Make the most of your stay')} />
          <div className="mt-6 bg-white rounded-2xl border border-zinc-200/70 overflow-hidden">
            {stayItems.map((item, idx) => (
              <TaskRow
                key={item.key}
                item={item}
                first={idx === 0}
                last={idx === stayItems.length - 1 && !loyaltyProgram}
                onClick={() => navigate(`/portal/home/${SLUG_FOR_KEY[item.key]}`)}
              />
            ))}
            {loyaltyProgram && (
              <LoyaltyRow
                program={loyaltyProgram}
                discountPct={loyaltyDiscount}
                enrolled={loyaltyAlreadyEnrolled}
                first={stayItems.length === 0}
                onClick={() => navigate('/portal/home/loyalty')}
              />
            )}
          </div>
        </section>
      )}

      {loyaltyProgram && !loyaltyAlreadyEnrolled && showLoyaltyModal && (
        <LoyaltyWelcomeModal
          program={loyaltyProgram}
          onSignUp={() => {
            setShowLoyaltyModal(false);
            navigate('/portal/home/loyalty');
          }}
          onDontShowAgain={() => {
            const key = loyaltyDismissedKey(payload?.reservationId);
            if (key) localStorage.setItem(key, '1');
            setShowLoyaltyModal(false);
          }}
          onClose={() => setShowLoyaltyModal(false)}
        />
      )}

      {items.length === 0 && arrival && daysOut !== null && (
        <section className="px-4 sm:px-6 lg:px-10 pt-12 lg:pt-16">
          <div className="bg-white rounded-2xl border border-zinc-200/70 p-8 lg:p-10 max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
              {t('Nothing to do right now')}
            </div>
            <h2 className="mt-2 text-[22px] font-semibold text-zinc-900 tracking-tight">
              {daysOut > 0
                ? t('You’re all squared away.')
                : daysOut === 0
                  ? t('Have a wonderful stay.')
                  : t('Enjoy the rest of your stay.')}
            </h2>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed max-w-lg">
              {t(
                'Your host hasn’t added any pre-arrival tasks, a guidebook, or add-ons for this reservation. This page will refresh automatically if anything new becomes available.'
              )}
            </p>
            <p className="mt-6 text-[13px] text-zinc-500">
              {t('Questions? Reply to the message from your host anytime.')}
            </p>
          </div>
        </section>
      )}

      <div className="pb-16" />
    </>
  );
}

function SectionHeader({
  eyebrow,
  title,
  progress,
}: {
  eyebrow: string;
  title: string;
  progress?: { completed: number; total: number };
}) {
  const { t } = useTranslation('portal');
  const showChip = !!progress && progress.total > 0 && progress.completed < progress.total;
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
        {eyebrow}
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <h2 className="text-[22px] sm:text-[26px] font-semibold text-zinc-900 tracking-tight">
          {title}
        </h2>
        {showChip && (
          <span className="sm:hidden shrink-0 text-[11px] font-medium uppercase tracking-[0.1em] text-zinc-500 tabular-nums bg-zinc-100 rounded-full px-2.5 py-1">
            {t('{{completed}}/{{total}} done', { completed: progress.completed, total: progress.total })}
          </span>
        )}
      </div>
    </div>
  );
}

function DateBlock({
  label,
  dateStr,
  timeStr,
}: {
  label: string;
  dateStr: string;
  timeStr?: string;
}) {
  const { t } = useTranslation('portal');
  return (
    <div className="flex-1 min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500">{label}:</div>
      <div className="mt-1 text-[14px] sm:text-base font-semibold text-[#1E3A5F] leading-snug">
        <span className="whitespace-nowrap">{dateStr}</span>
      </div>
      {timeStr && (
        <div className="mt-0.5 text-[13px] sm:text-sm text-gray-500 font-normal leading-tight">
          {t('at {{time}}', { time: timeStr })}
        </div>
      )}
    </div>
  );
}

function LoyaltyRow({
  program,
  discountPct,
  enrolled,
  first,
  onClick,
}: {
  program: LoyaltyProgram;
  discountPct: number | null;
  enrolled: boolean;
  first: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('portal');
  const blurb = enrolled
    ? t("You're enrolled, your perks are active for this stay")
    : discountPct
      ? t('Save {{pct}}% on every stay, plus member perks', { pct: discountPct })
      : t('Members get exclusive perks on every stay');

  return (
    <button
      onClick={onClick}
      className={`group w-full text-left flex items-center gap-2.5 sm:gap-4 px-3.5 sm:px-5 lg:px-6 py-4 sm:py-5 hover:bg-zinc-50/80 transition ${
        !first ? 'border-t border-zinc-100' : ''
      }`}
    >
      <div
        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          enrolled ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
        }`}
      >
        {enrolled ? (
          <Check className="w-[18px] h-[18px] sm:w-5 sm:h-5" strokeWidth={2.5} />
        ) : (
          <Sparkles className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-medium text-zinc-900 text-[15px] sm:text-base">
            {program.name || t('Loyalty program')}
          </h3>
          {enrolled && (
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-emerald-600">
              {t('Member')}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[13px] sm:text-sm text-zinc-500 line-clamp-2">{blurb}</p>
      </div>
      <div className="flex-shrink-0 text-sm font-medium text-zinc-400 group-hover:text-[#1E3A5F] transition flex items-center gap-1 pl-1">
        <span className="hidden sm:inline">{enrolled ? t('View') : t('Join')}</span>
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}

function TaskRow({
  item,
  first,
  last,
  onClick,
}: {
  item: PortalItem;
  first: boolean;
  last: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('portal');
  const meta = getMeta(item.key, t);
  const Icon = meta.icon;
  const isCompleted = item.status === 'completed';
  const isInProgress = item.status === 'in_progress';

  return (
    <button
      onClick={onClick}
      className={`group w-full text-left flex items-center gap-2.5 sm:gap-4 px-3.5 sm:px-5 lg:px-6 py-4 sm:py-5 hover:bg-zinc-50/80 transition ${
        !first ? 'border-t border-zinc-100' : ''
      } ${last ? '' : ''}`}
    >
      <div
        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          isCompleted ? 'bg-emerald-50 text-emerald-600' : 'bg-zinc-100 text-zinc-700'
        }`}
      >
        {isCompleted ? (
          <Check className="w-[18px] h-[18px] sm:w-5 sm:h-5" strokeWidth={2.5} />
        ) : (
          <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-medium text-zinc-900 text-[15px] sm:text-base">{item.title}</h3>
          {isInProgress && (
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-600">
              {t('In progress')}
            </span>
          )}
          {isCompleted && (
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-emerald-600">
              {t('Done')}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[13px] sm:text-sm text-zinc-500 line-clamp-2">{item.blurb || meta.blurb}</p>
      </div>
      <div className="flex-shrink-0 text-sm font-medium text-zinc-400 group-hover:text-[#1E3A5F] transition flex items-center gap-1 pl-1">
        <span className="hidden sm:inline">{isCompleted ? t('View') : meta.cta}</span>
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}

